import { Connector } from '../types';
import { CONNECTOR, CONNECTOR_PINS } from './constants';
import { Point, connectorCenter } from './geometry';
import { connectorLayout, PinWireCounts } from './connectorLayout';

const HALF_WIDTH = CONNECTOR.WIDTH / 2;

/** Index of a pin on a connector, or -1 when unassigned or unknown. */
export function pinIndex(connector: Connector, pinId?: string): number {
  if (!pinId) {
    return -1;
  }
  return (connector.pins ?? []).findIndex(p => p.id === pinId);
}

/**
 * World Y of a pin's row centre, falling back to the connector centre when
 * unassigned. `counts` describes how many wires each pin carries, so rows that
 * expand for multi-wire pins stay aligned with the rendered connector.
 */
export function pinRowY(connector: Connector, pinId?: string, counts: PinWireCounts = {}): number {
  const row = connectorLayout(connector.pins ?? [], counts).rows.find(r => r.pinId === pinId);
  if (!row) {
    return connectorCenter(connector).y;
  }
  return connector.yPosition + CONNECTOR_PINS.BODY_Y + row.centerY;
}

/** Horizontal side (+1 right, -1 left) of the connector that faces `toward`. */
function side(connector: Connector, toward: Point): number {
  return toward.x >= connectorCenter(connector).x ? 1 : -1;
}

/**
 * Where a wire on `pinId` meets the connector edge facing `toward`, aligned to
 * the pin's row. Individual wires fan out from these anchors.
 */
export function connectorAnchor(connector: Connector, pinId: string | undefined, toward: Point, counts: PinWireCounts = {}): Point {
  return { x: connectorCenter(connector).x + side(connector, toward) * HALF_WIDTH, y: pinRowY(connector, pinId, counts) };
}

/**
 * The point just past the connector edge (toward `toward`) where the fanned
 * wires merge into the shared trunk line.
 */
export function connectorBreakout(connector: Connector, toward: Point, gap: number): Point {
  const center = connectorCenter(connector);
  return { x: center.x + side(connector, toward) * (HALF_WIDTH + gap), y: center.y };
}

/** Left-edge anchor at a pin's row, used for same-connector jumper bows. */
export function pinEdgeAnchor(connector: Connector, pinId?: string, counts: PinWireCounts = {}): Point {
  return { x: connector.xPosition, y: pinRowY(connector, pinId, counts) };
}
